import React from "react";
import { useTranslation } from "react-i18next";
import { Info, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useRetryScreeningReportGenerationMutation } from "@/features/patients/infrastructure/api/patientsApi";
import {
  REPORT_FAILED_STATUS,
  REPORT_GENERATING_STATUS,
  REPORT_PENDING_STATUSES,
} from "@/features/patients/infrastructure/data/reportGeneration";

interface Props {
  patientId: string;
  assessmentId: string;
  status: string;
  onRetrySuccess?: () => void;
}

const ReportGenerationBanner: React.FC<Props> = ({
  patientId,
  assessmentId,
  status,
  onRetrySuccess,
}) => {
  const { t } = useTranslation("patients");
  const [retry, { isLoading: isRetrying }] =
    useRetryScreeningReportGenerationMutation();

  const handleTrigger = async (label: string) => {
    try {
      await retry({ patientId, assessmentId }).unwrap();
      toast.success(label);
      onRetrySuccess?.();
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        t("reportBanner.retryError");
      toast.error(message);
    }
  };

  if (REPORT_PENDING_STATUSES.has(status)) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Info className="mt-0.5 size-5 shrink-0 text-yellow-700" />
          <div className="min-w-0">
            <h3 className="text-[length:var(--type-body-font-size)] leading-[var(--type-body-line-height)] text-yellow-800 [font-weight:700]">
              {t("reportBanner.pendingTitle")}
            </h3>
            <p className="text-[length:var(--type-body-font-size)] leading-[var(--type-body-line-height)] text-yellow-700 [font-weight:400]">
              {t("reportBanner.pendingBody")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === REPORT_GENERATING_STATUS) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-primary bg-[color-mix(in_oklab,var(--primary)_10%,white)] p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Info className="mt-0.5 size-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <h3 className="text-[length:var(--type-body-font-size)] leading-[var(--type-body-line-height)] text-primary [font-weight:700]">
              {t("reportBanner.queuedTitle")}
            </h3>
            <p className="text-[length:var(--type-body-font-size)] leading-[var(--type-body-line-height)] text-primary [font-weight:400]">
              {t("reportBanner.queuedBody")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === REPORT_FAILED_STATUS) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-destructive-text bg-[color-mix(in_oklab,var(--destructive-text)_10%,white)] p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <XCircle className="mt-0.5 size-5 shrink-0 text-destructive-text" />
          <div className="min-w-0">
            <h3 className="text-[length:var(--type-body-font-size)] leading-[var(--type-body-line-height)] text-destructive-text [font-weight:700]">
              {t("reportBanner.failedTitle")}
            </h3>
            <p className="text-[length:var(--type-body-font-size)] leading-[var(--type-body-line-height)] text-destructive-text [font-weight:400]">
              {t("reportBanner.failedBody")}
            </p>
          </div>
        </div>
        <Button
          className="w-full shrink-0 cursor-pointer sm:w-auto"
          loading={isRetrying}
          onClick={() => void handleTrigger(t("reportBanner.retriedToast"))}
        >
          {t("reportBanner.retry")}
        </Button>
      </div>
    );
  }

  return null;
};

export default ReportGenerationBanner;
